<!-- PhotoViewer — the real product photos, full screen.

     A horizontal scroll-snap track, so a swipe is the platform's own swipe
     (momentum, rubber band, no gesture library). ←/→ and the two chevrons step
     one photo; Esc or ✕ closes. Opens on the photo that was tapped. -->
<template>
  <IonModal
    :is-open="open"
    class="photo-viewer"
    :aria-label="`Photos of ${title}`"
    @did-present="onPresent"
    @did-dismiss="emit('close')"
  >
    <div class="pv" @keydown="onKey">
      <div class="pv__bar">
        <span class="pv__count" aria-live="polite">{{ index + 1 }} / {{ images.length }}</span>
        <button ref="closeBtn" type="button" class="pv__btn" aria-label="Close photos" @click="emit('close')">
          <IonIcon :icon="closeOutline" aria-hidden="true" />
        </button>
      </div>

      <div ref="track" class="pv__track" tabindex="-1" @scroll.passive="onScroll">
        <figure v-for="(src, i) in images" :key="src" class="pv__slide">
          <img
            :src="imgUrl(src, 1024)"
            :alt="`${title}, photo ${i + 1} of ${images.length}`"
            :loading="Math.abs(i - index) <= 1 ? 'eager' : 'lazy'"
            decoding="async"
            draggable="false"
          >
        </figure>
      </div>

      <template v-if="images.length > 1">
        <button type="button" class="pv__btn pv__nav pv__nav--prev" :disabled="index === 0" aria-label="Previous photo" @click="go(index - 1)">
          <IonIcon :icon="chevronBack" aria-hidden="true" />
        </button>
        <button type="button" class="pv__btn pv__nav pv__nav--next" :disabled="index === images.length - 1" aria-label="Next photo" @click="go(index + 1)">
          <IonIcon :icon="chevronForward" aria-hidden="true" />
        </button>
      </template>
    </div>
  </IonModal>
</template>

<script setup>
import { ref, nextTick } from 'vue';
import { IonModal, IonIcon } from '@ionic/vue';
import { closeOutline, chevronBack, chevronForward } from 'ionicons/icons';
import { imgUrl, reducedMotion } from '../store.js';

const props = defineProps({
  open: { type: Boolean, default: false },
  images: { type: Array, default: () => [] },
  start: { type: Number, default: 0 },
  title: { type: String, default: '' },
});
const emit = defineEmits(['close']);

const track = ref(null);
const closeBtn = ref(null);
const index = ref(0);

async function onPresent () {
  index.value = Math.max(0, Math.min(props.images.length - 1, props.start));
  await nextTick();
  const el = track.value;
  if (el) el.scrollLeft = el.clientWidth * index.value;
  closeBtn.value?.focus();
}

function onScroll () {
  const el = track.value;
  if (!el || !el.clientWidth) return;
  index.value = Math.round(el.scrollLeft / el.clientWidth);
}

function go (i) {
  const el = track.value;
  if (!el) return;
  const n = Math.max(0, Math.min(props.images.length - 1, i));
  el.scrollTo({ left: el.clientWidth * n, behavior: reducedMotion.value ? 'auto' : 'smooth' });
  index.value = n;
}

function onKey (e) {
  if (e.key === 'ArrowRight') { e.preventDefault(); go(index.value + 1); }
  else if (e.key === 'ArrowLeft') { e.preventDefault(); go(index.value - 1); }
}
</script>

<style scoped>
/* The photos on the ink, the app's own near-black green, not a generic black. */
.pv {
  position: relative;
  height: 100%;
  background: var(--ink, #1F2A22);
  color: var(--dock, #FAF7F1);
}

.pv__bar {
  position: absolute;
  inset: 0 0 auto;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(env(safe-area-inset-top, 0px) + var(--sp-2)) var(--sp-3) var(--sp-2) var(--sp-5);
  background: linear-gradient(color-mix(in srgb, var(--ink, #1F2A22) 70%, transparent), transparent);
}
.pv__count {
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-size: var(--text-xs);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
}

.pv__btn {
  display: grid;
  place-items: center;
  width: var(--tap);
  height: var(--tap);
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: color-mix(in srgb, var(--dock, #FAF7F1) 14%, transparent);
  color: var(--dock, #FAF7F1);
  font-size: var(--text-xl);
  cursor: pointer;
}
.pv__btn:focus-visible { outline: 2px solid var(--mustard, #F2C230); outline-offset: 2px; }
.pv__btn:disabled { opacity: 0.3; cursor: default; }

.pv__track {
  display: flex;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  outline: none;
}
.pv__track::-webkit-scrollbar { display: none; }

.pv__slide {
  flex: 0 0 100%;
  display: grid;
  place-items: center;
  margin: 0;
  padding: calc(env(safe-area-inset-top, 0px) + var(--sp-8)) 0 calc(env(safe-area-inset-bottom, 0px) + var(--sp-7));
  scroll-snap-align: center;
  scroll-snap-stop: always;
}
.pv__slide img {
  max-width: calc(100% - var(--sp-5));
  border-radius: var(--radius);
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
}

.pv__nav {
  position: absolute;
  top: 50%;
  z-index: 2;
  transform: translateY(-50%);
}
.pv__nav--prev { left: var(--sp-3); }
.pv__nav--next { right: var(--sp-3); }
/* On a phone the swipe is the control; the chevrons are for a mouse. */
@media (hover: none) and (pointer: coarse) {
  .pv__nav { display: none; }
}
</style>

<style>
ion-modal.photo-viewer { --width: 100%; --height: 100%; --border-radius: 0; --background: var(--ink, #1F2A22); }
</style>
